import { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import {
  Card,
  CardContent,
  Badge,
  Button,
  Checkbox,
  Collapsible,
  CollapsibleContent,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Switch,
  Separator,
} from '@roger/ui-components';
import { ChevronDown, ChevronRight, Loader2, Save } from 'lucide-react';
import type {
  WorksheetGoal,
  WorksheetIntervention,
} from '../../types/worksheetGoals.types';
import {
  updateWorksheetGoal,
  getWorksheetInterventions,
} from '../../services/rogerDocApi';
import { useToast } from '../../hooks/useToastContext';
import InstructionDisplay from '../InstructionDisplay';
import InterventionItem from './InterventionItem';
import { getDisciplineColor } from '../../utils/worksheetGoals';

export interface GoalItemRef {
  hasChanges: boolean;
  save: (suppressToast?: boolean) => Promise<void>;
  isSaving: boolean;
}

interface GoalItemProps {
  goal: WorksheetGoal;
  clinicName?: string;
  isExpanded?: boolean;
  onToggleExpanded?: () => void;
  onChangeStateUpdate?: (goalId: string, hasChanges: boolean) => void;
  onGoalUpdate?: (updatedGoal: WorksheetGoal) => void;
  onSaveSuccess?: (goalName: string) => void;
  allowedCreationTypes?: string[];
}

const GoalItem = forwardRef<GoalItemRef, GoalItemProps>(
  (
    {
      goal,
      clinicName,
      isExpanded,
      onToggleExpanded,
      onChangeStateUpdate,
      onGoalUpdate,
      onSaveSuccess,
      allowedCreationTypes = [],
    },
    ref
  ) => {
    const [interventionsOpen, setInterventionsOpen] = useState(false);
    const [goalEdits, setGoalEdits] = useState<Partial<WorksheetGoal>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [interventions, setInterventions] = useState<WorksheetIntervention[]>(
      []
    );
    const [interventionsLoading, setInterventionsLoading] = useState(false);
    const [interventionsLoaded, setInterventionsLoaded] = useState(false);
    const { error } = useToast();

    // Use internal state if no external control is provided
    const [internalExpanded, setInternalExpanded] = useState(false);
    const expanded = isExpanded !== undefined ? isExpanded : internalExpanded;
    const toggleExpanded =
      onToggleExpanded || (() => setInternalExpanded(!internalExpanded));

    // Check if there are any changes
    const hasChanges = Object.keys(goalEdits).length > 0;

    // Get the current value of a property (original + any edits)
    const getCurrentValue = <K extends keyof WorksheetGoal>(
      property: K
    ): WorksheetGoal[K] => {
      return property in goalEdits && goalEdits[property] !== undefined
        ? (goalEdits[property] as WorksheetGoal[K])
        : goal[property];
    };

    // Handle required checkbox change
    const handleRequiredChange = (checked: boolean) => {
      setGoalEdits(prev => {
        const newEdits = { ...prev };

        if (checked === goal.required) {
          delete newEdits.required;
        } else {
          newEdits.required = checked;
        }

        return newEdits;
      });
    };

    // Handle active toggle change
    const handleActiveChange = (checked: boolean) => {
      setGoalEdits(prev => {
        const newEdits = { ...prev };

        if (checked === goal.active) {
          delete newEdits.active;
        } else {
          newEdits.active = checked;
        }

        return newEdits;
      });
    };

    // Get current used_by values (original + any edits)
    const currentUsedBy = getCurrentValue('used_by') || [];

    // Handle used_by checkbox change
    const handleUsedByChange = (creationType: string, checked: boolean) => {
      setGoalEdits(prev => {
        const newEdits = { ...prev };
        const currentValues = prev.used_by || goal.used_by || [];

        let newUsedBy: string[];
        if (checked) {
          // Add the creation type if not already present
          newUsedBy = currentValues.includes(creationType)
            ? currentValues
            : [...currentValues, creationType];
        } else {
          // Remove the creation type
          newUsedBy = currentValues.filter(type => type !== creationType);
        }

        // If the new value is the same as original, remove it from edits
        const originalUsedBy = goal.used_by || [];
        const isSameAsOriginal =
          newUsedBy.length === originalUsedBy.length &&
          newUsedBy.every(type => originalUsedBy.includes(type)) &&
          originalUsedBy.every(type => newUsedBy.includes(type));

        if (isSameAsOriginal) {
          delete newEdits.used_by;
        } else {
          newEdits.used_by = newUsedBy;
        }

        return newEdits;
      });
    };

    // Handle save button click
    const handleSave = async (suppressToast = false) => {
      if (!hasChanges) return;

      setIsSaving(true);
      try {
        const updatedGoal = { ...goal, ...goalEdits };
        const savedGoal = await updateWorksheetGoal(updatedGoal);

        if (onGoalUpdate) {
          onGoalUpdate(savedGoal);
        }

        setGoalEdits({});

        if (onSaveSuccess && !suppressToast) {
          onSaveSuccess(goal.goal_name || 'Unnamed Goal');
        }
      } catch (err) {
        console.error('Failed to save goal:', err);
        error(
          'Save Failed',
          `Failed to save goal: ${goal.goal_name || 'Unnamed Goal'}`
        );
      } finally {
        setIsSaving(false);
      }
    };

    // Fetch interventions when expanded
    const fetchInterventions = async () => {
      if (
        !goal.attached_interventions ||
        goal.attached_interventions.length === 0
      ) {
        return;
      }

      setInterventionsLoading(true);
      try {
        const interventionsData = await getWorksheetInterventions(
          goal.clinic_key,
          goal.attached_interventions
        );

        setInterventions(interventionsData);
        setInterventionsLoaded(true);
      } catch (err) {
        console.error('Failed to load interventions:', err);
        setInterventions([]);
      } finally {
        setInterventionsLoading(false);
      }
    };

    // Handle intervention toggle
    const handleInterventionToggle = () => {
      const newState = !interventionsOpen;
      setInterventionsOpen(newState);

      if (newState && !interventionsLoaded) {
        fetchInterventions();
      }
    };

    // Notify parent when change state updates
    useEffect(() => {
      if (onChangeStateUpdate) {
        onChangeStateUpdate(goal._id || goal.goal_key, hasChanges);
      }

      return () => {
        if (onChangeStateUpdate) {
          onChangeStateUpdate(goal._id || goal.goal_key, false);
        }
      };
    }, [hasChanges, onChangeStateUpdate, goal._id, goal.goal_key]);

    // Expose save function and state to parent component
    useImperativeHandle(ref, () => ({
      hasChanges,
      save: handleSave,
      isSaving,
    }));

    return (
      <Card className='border border-gray-200'>
        <CardContent>
          <div className='space-y-4'>
            {/* Header with Goal Name, Toggle, and Save Button */}
            <div className='flex justify-between items-start gap-4'>
              <div
                className='flex items-center gap-2 flex-1 cursor-pointer'
                onClick={toggleExpanded}
              >
                <div className='text-gray-600'>
                  {expanded ? (
                    <ChevronDown className='h-4 w-4' />
                  ) : (
                    <ChevronRight className='h-4 w-4' />
                  )}
                </div>
                <div className='text-lg font-semibold text-gray-900'>
                  {goal.goal_name || 'Unnamed Goal'}
                </div>
              </div>
              <Button
                size='sm'
                variant={hasChanges ? 'default' : 'outline'}
                disabled={!hasChanges}
                onClick={() => handleSave(false)}
              >
                {isSaving ? (
                  <>
                    <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                    Saving
                  </>
                ) : (
                  <>
                    <Save className='h-4 w-4 mr-2' />
                    Save
                  </>
                )}
              </Button>
            </div>

            {/* Summary Information */}
            <div
              className='flex flex-wrap items-center gap-2 cursor-pointer'
              onClick={toggleExpanded}
            >
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className='flex items-center gap-2'
                      onClick={e => e.stopPropagation()}
                    >
                      <span className='text-sm font-medium text-gray-700'>
                        {getCurrentValue('active') ? 'Active' : 'Inactive'}
                      </span>
                      <Switch
                        checked={getCurrentValue('active')}
                        onCheckedChange={handleActiveChange}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className='max-w-xs text-sm'>
                      Toggle active status. If a goal is inactive it will never
                      be assigned to any patient
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <Badge
                variant='outline'
                className={getDisciplineColor(goal.discipline.discipline_name)}
              >
                {goal.discipline.discipline_name || 'Unknown'}
              </Badge>

              <Badge
                variant='outline'
                className={
                  getCurrentValue('required')
                    ? 'bg-orange-100 text-orange-800 border-orange-200'
                    : 'bg-gray-100 text-gray-800 border-gray-200'
                }
              >
                {getCurrentValue('required') ? 'Required' : 'Optional'}
              </Badge>

              <Badge
                variant='outline'
                className='bg-indigo-100 text-indigo-800 border-indigo-200'
              >
                {goal.intervention_count} intervention
                {goal.intervention_count !== 1 ? 's' : ''}
              </Badge>

              {clinicName && (
                <span className='text-sm text-gray-600'>
                  Clinic: <span className='font-medium'>{clinicName}</span>
                </span>
              )}
            </div>

            {/* Expanded Content */}
            <Collapsible open={expanded}>
              <CollapsibleContent>
                <Separator className='my-4' />
                <div className='space-y-4'>
                  {/* Required Checkbox */}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className='flex items-center'>
                          <Checkbox
                            checked={getCurrentValue('required')}
                            onCheckedChange={handleRequiredChange}
                            id={`required-${goal._id}`}
                          />
                          <label
                            htmlFor={`required-${goal._id}`}
                            className='ml-2 text-sm font-medium cursor-pointer'
                          >
                            Required
                          </label>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className='max-w-xs text-sm'>
                          If a goal is required it will automatically be
                          assigned to all patients in the goal's specialty
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  {/* Used By (Creation Types) */}
                  {allowedCreationTypes.length > 0 && (
                    <div className='mb-4'>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className='text-sm font-medium text-gray-700 mb-2 cursor-help w-fit'>
                              Used By:
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className='max-w-xs text-sm'>
                              Select which visit types this goal should be used
                              for
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <div className='flex flex-col gap-2'>
                        {allowedCreationTypes.map((creationType, index) => (
                          <div key={index} className='flex items-center'>
                            <Checkbox
                              checked={currentUsedBy.includes(creationType)}
                              onCheckedChange={checked =>
                                handleUsedByChange(creationType, !!checked)
                              }
                              id={`used-by-${goal._id}-${index}`}
                            />
                            <label
                              htmlFor={`used-by-${goal._id}-${index}`}
                              className='ml-2 text-sm text-gray-700 cursor-pointer'
                            >
                              {creationType}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Instruction */}
                  {goal.instruction && (
                    <div>
                      <div className='text-sm font-medium text-gray-700 mb-2'>
                        Instruction:
                      </div>
                      <InstructionDisplay instruction={goal.instruction} />
                    </div>
                  )}

                  {/* Attached Interventions */}
                  <div>
                    <div className='flex items-center gap-2 mb-2'>
                      <span className='text-sm font-medium text-gray-700'>
                        Attached Interventions
                      </span>
                      {goal.intervention_count > 0 && (
                        <Badge variant='outline' className='text-xs'>
                          {goal.intervention_count}
                        </Badge>
                      )}
                    </div>

                    {goal.intervention_count === 0 ? (
                      <p className='text-sm text-gray-500 italic'>
                        No interventions attached to this goal
                      </p>
                    ) : (
                      <>
                        <Button
                          variant={interventionsOpen ? 'default' : 'outline'}
                          size='sm'
                          onClick={handleInterventionToggle}
                          className='mb-2'
                        >
                          {interventionsOpen ? (
                            <>
                              <ChevronDown className='h-4 w-4 mr-2' />
                              Hide Interventions
                            </>
                          ) : (
                            <>
                              <ChevronRight className='h-4 w-4 mr-2' />
                              Show Interventions
                            </>
                          )}
                        </Button>

                        <Collapsible open={interventionsOpen}>
                          <CollapsibleContent>
                            {interventionsLoading ? (
                              <div className='flex items-center gap-2 p-4 bg-gray-50 rounded-md'>
                                <Loader2 className='h-4 w-4 animate-spin' />
                                <span className='text-sm text-gray-600'>
                                  Loading interventions...
                                </span>
                              </div>
                            ) : interventions.length > 0 ? (
                              <div className='space-y-2'>
                                {interventions.map(intervention => (
                                  <InterventionItem
                                    key={intervention._id}
                                    intervention={intervention}
                                  />
                                ))}
                              </div>
                            ) : (
                              <p className='text-sm text-gray-500 italic text-center p-4 bg-gray-50 rounded-md'>
                                Unable to load intervention details
                              </p>
                            )}
                          </CollapsibleContent>
                        </Collapsible>
                      </>
                    )}
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </CardContent>
      </Card>
    );
  }
);

GoalItem.displayName = 'GoalItem';

export default GoalItem;
